import { Edge, type Node, internalsSymbol } from 'reactflow';
import { z } from 'zod';
import { NodeDataSchema } from '@duke-hq/svc-api/student/trpc/routers/resourceEnrolments/helpers/flowContentSchemas';
import type { NodeData } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/flowEditorBlockNodeTypes';
import { BLOCK_NODE_WIDTH } from '../constants';

export const nodeColor = (node: Node): string => {
  const nodeTypeColors: Record<string, string> = {
    start: '#FF0000',
    end: '#0047AB',
  };

  return (node.type && nodeTypeColors[node.type]) || '#FFDF00';
};

export const getIsStartNode = (currentNode: Node) => {
  return currentNode.type === 'start';
};

// The persona a new block branching off this node should inherit: the persona of
// the node's last message bubble. New blocks created by dragging an edge off a
// node continue the conversation, so they should speak as that node's last
// speaker rather than resetting to the library default. Returns null when the
// node has no message with a persona (a fresh/empty node).
export const lastPersonaOfNode = (node: Node<NodeData> | undefined): string | null => {
  const blocks = node?.data?.tiptap?.content;
  if (!Array.isArray(blocks)) {
    return null;
  }
  for (let i = blocks.length - 1; i >= 0; i--) {
    const personaId = blocks[i]?.attrs?.personaId;
    if (typeof personaId === 'string' && personaId.length > 0) {
      return personaId;
    }
  }
  return null;
};

// Centre the viewport on a node at a readable zoom. Used on first open; find
// jumps animate by hand instead (see FindBar/viewportPan).
export const centreOnNode = (
  setCenter: (x: number, y: number, options?: { zoom?: number; duration?: number }) => void,
  node: Pick<Node, 'position' | 'width' | 'height'>,
  options?: { zoom?: number; duration?: number },
): void => {
  const width = node.width ?? BLOCK_NODE_WIDTH;
  const height = node.height ?? 0;
  setCenter(node.position.x + width / 2, node.position.y + height / 2, {
    zoom: options?.zoom ?? 1,
    duration: options?.duration,
  });
};

export const computeEndNodeIds = (allNodes: Node[], allEdges: Edge[]): string[] => {
  const nodesWithOutgoingEdges = new Set(allEdges.map((e) => e.source));
  const nodesWithIncomingEdges = new Set(allEdges.map((e) => e.target));
  return allNodes
    .filter((node) => !nodesWithOutgoingEdges.has(node.id) && nodesWithIncomingEdges.has(node.id))
    .map((node) => node.id);
};

type WithOutgoers = { outgoers?: string[]; options?: unknown[] };

// A node should render the outgoing menu (the + button) when it has no
// outgoing edges and no options panel of its own (options handle their own
// outgoing connections).
export const shouldRenderOutgoingMenu = (node: Node<WithOutgoers> | undefined): boolean => {
  if (!node) {
    return false;
  }
  return !node.data.outgoers?.length && !node.data.options;
};

// Whether dropping a new connected node from a source handle is allowed. The
// drop-to-create path bypasses React Flow's isValidConnection (which only runs
// for drops onto a target handle), so this enforces the same branching rule: a
// reply-button options block may lead to one node per option handle, while a
// plain block may lead to a single node. Without it, dragging from an
// already-connected option handle would fan one option out to many nodes.
export const canDropNewNodeFromHandle = (
  sourceNode: Node<WithOutgoers> | undefined,
  edges: Pick<Edge, 'source' | 'sourceHandle'>[],
  handleId: string | null,
): boolean => {
  if (!sourceNode) {
    return false;
  }
  const sourceEdges = edges.filter((edge) => edge.source === sourceNode.id);
  if (sourceNode.data.options?.length) {
    return !sourceEdges.some((edge) => edge.sourceHandle === handleId);
  }
  return sourceEdges.length === 0;
};

// Set the outgoers on a single node by id. Used after onConnect/onEdgesDelete
// to keep node.data.outgoers in sync with the edges.
export const setOutgoersForNode = <T extends WithOutgoers>(
  allNodes: Node<T>[],
  nodeId: string,
  outgoers: string[],
): Node<T>[] =>
  allNodes.map((node) => {
    if (node.id !== nodeId) {
      return node;
    }
    return { ...node, data: { ...node.data, outgoers } };
  });

// Recompute a single node's outgoers from the edges, against the given node
// list. Used by the drop-to-create path, where the source's outgoers must be
// derived from a node array that already includes the just-added target. It
// must operate on that array rather than React Flow's store (via getNodes),
// which only syncs on the next render and so would not yet hold the new node,
// leaving the source's outgoers empty and its add-node menu wrongly shown.
export const recomputeOutgoersForNode = <T extends WithOutgoers>(
  allNodes: Node<T>[],
  nodeId: string,
  allEdges: Pick<Edge, 'source' | 'target'>[],
): Node<T>[] => {
  const nodeIds = new Set(allNodes.map((node) => node.id));
  const outgoers = allEdges
    .filter((edge) => edge.source === nodeId && nodeIds.has(edge.target))
    .map((edge) => edge.target);
  return setOutgoersForNode(allNodes, nodeId, outgoers);
};

// When nodes are deleted, any other node whose outgoers reference a deleted
// node has those references cleaned up.
export const removeOutgoersForDeletedNodes = <T extends WithOutgoers>(
  allNodes: Node<T>[],
  deletedNodeIds: Set<string>,
): Node<T>[] =>
  allNodes.map((node) => {
    if (!node.data.outgoers?.some((id) => deletedNodeIds.has(id))) {
      return node;
    }
    return {
      ...node,
      data: {
        ...node.data,
        outgoers: node.data.outgoers.filter((id) => !deletedNodeIds.has(id)),
      },
    };
  });

const PositionSchema = z.object({ x: z.number(), y: z.number() });

// React Flow's internal fields we use directly. `looseObject` carries any
// other React Flow internals (`dragging`, `style`, `dragHandle` etc.)
// through unchanged.
const FlowNodeSchema = z.looseObject({
  id: z.string(),
  type: z.string().optional(),
  position: PositionSchema,
  width: z.number().nullish(),
  height: z.number().nullish(),
  data: NodeDataSchema,
});

const FlowEdgeSchema = z.looseObject({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  sourceHandle: z.string().nullish(),
  targetHandle: z.string().nullish(),
  type: z.string().optional(),
});

const FlowContentSchema = z.object({
  nodes: z.array(FlowNodeSchema),
  edges: z.array(FlowEdgeSchema),
});

// The edge type every flow edge renders with: the custom ButtonEdge (a bezier
// path with a mid-edge delete button), registered under this key in
// FlowEditor's edgeTypes. Flows saved before this became the default still carry
// the old built-in 'smoothstep' type, so we normalise on load (see
// parseFlowContent) to keep old and new edges rendering consistently.
export const FLOW_EDGE_TYPE = 'deletable';

// Parses the JSON blob stored in Prisma into typed nodes and edges. Throws
// on shape mismatch, which is intentional: a corrupt flow can't be edited
// safely, and crashing at load gives a clearer error than crashing during
// render. Mirrors `parseConversationFlowContent` in the student app.
export const parseFlowContent = (content: unknown): { nodes: Node<NodeData>[]; edges: Edge[] } => {
  const { nodes, edges } = FlowContentSchema.parse(content ?? { nodes: [], edges: [] });
  return {
    nodes,
    // Coerce every edge to the current type so flows saved with the old
    // built-in 'smoothstep' type render the same custom ButtonEdge as new ones.
    // They persist as FLOW_EDGE_TYPE on the next save and converge.
    edges: edges.map((edge) => ({ ...edge, type: FLOW_EDGE_TYPE })),
  };
};

// Older saved flows may have nodes without `data.outgoers`. We rely on it for
// per-node render checks (e.g. shouldRenderOutgoingMenu), so fill it in from
// the edges on initial load when missing. Edges pointing at a target that no
// node has are ignored, the same way reactflow's getOutgoers resolves targets
// against real nodes: a stale edge (e.g. a starter flow's placeholder edge to a
// node that was never created) must not count as an outgoer, or the source node
// looks connected and loses its add-node menu.
export const hydrateOutgoers = <T extends { outgoers?: string[] }>(
  allNodes: Node<T>[],
  allEdges: Edge[],
): Node<T>[] => {
  const nodeIds = new Set(allNodes.map((node) => node.id));
  const outgoersById = new Map<string, string[]>();
  for (const edge of allEdges) {
    if (!nodeIds.has(edge.target)) {
      continue;
    }
    const list = outgoersById.get(edge.source);
    if (list) {
      list.push(edge.target);
    } else {
      outgoersById.set(edge.source, [edge.target]);
    }
  }
  return allNodes.map((node) => {
    if (node.data.outgoers !== undefined) {
      return node;
    }
    return {
      ...node,
      data: { ...node.data, outgoers: outgoersById.get(node.id) ?? [] },
    };
  });
};

// Generous fallback height for a never-measured node. Over-estimating is safe:
// it only widens an edge's bounding box, so it can only keep an edge that a
// tighter box would cull, and the real height replaces it on mount.
const SEED_NODE_HEIGHT = 600;

// Clears React Flow's first edge-visibility gate (the size check) for off-screen
// nodes by seeding a width/height into the store without mounting them. Persisted
// nodes keep their measured size; we only fill the gap for never-rendered ones.
// Not sufficient alone: the renderer's second gate needs measured handle
// positions, which only exist after mount, so edges to still-unmounted nodes are
// drawn by the overlay (see `findOffscreenEdges` and `OffscreenEdges`).
export const seedNodeDimensions = <T,>(nodes: Node<T>[]): Node<T>[] =>
  nodes.map((node) => ({
    ...node,
    width: node.width ?? BLOCK_NODE_WIDTH,
    height: node.height ?? SEED_NODE_HEIGHT,
  }));

// React Flow records `handleBounds` once a node mounts and measures its handles.
// This is the same predicate its edge renderer uses to decide an edge is drawable.
const hasMeasuredHandles = (node: Node | undefined): boolean =>
  node?.[internalsSymbol]?.handleBounds != null;

// Absolute centre of a node's handle, mirroring React Flow's `getHandlePosition`.
// Uses the measured handle box when mounted (pixel-accurate), else falls back to
// the node's edge centre. Source handles are on the right, targets on the left.
const handlePoint = (
  node: Node,
  handleType: 'source' | 'target',
  handleId: string | null | undefined,
): { x: number; y: number } => {
  const nodeX = node.positionAbsolute?.x ?? node.position.x;
  const nodeY = node.positionAbsolute?.y ?? node.position.y;
  const nodeWidth = node.width ?? BLOCK_NODE_WIDTH;
  const nodeHeight = node.height ?? SEED_NODE_HEIGHT;

  const bounds = node[internalsSymbol]?.handleBounds?.[handleType];
  const handle = bounds?.find((h) => h.id === handleId) ?? bounds?.[0] ?? null;

  if (handle) {
    const x = nodeX + handle.x + (handleType === 'source' ? handle.width : 0);
    const y = nodeY + handle.y + handle.height / 2;
    return { x, y };
  }

  // No measured handle: place it at the centre of the relevant node edge.
  return handleType === 'source'
    ? { x: nodeX + nodeWidth, y: nodeY + nodeHeight / 2 }
    : { x: nodeX, y: nodeY + nodeHeight / 2 };
};

export interface OffscreenEdge {
  id: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  source: string;
  target: string;
}

// The edges React Flow drops under `onlyRenderVisibleElements`: those with
// exactly one endpoint mounted (both-mounted it draws itself; both-unmounted is
// fully off-screen). The mounted end gets precise geometry, the off-screen end
// an estimate from the node's persisted size.
export const findOffscreenEdges = (
  nodeInternals: Map<string, Node>,
  edges: Edge[],
): OffscreenEdge[] => {
  const result: OffscreenEdge[] = [];

  for (const edge of edges) {
    const sourceNode = nodeInternals.get(edge.source);
    const targetNode = nodeInternals.get(edge.target);
    if (!sourceNode || !targetNode) {
      continue;
    }

    const sourceMounted = hasMeasuredHandles(sourceNode);
    const targetMounted = hasMeasuredHandles(targetNode);
    // React Flow already drew it, or it is fully off-screen.
    if (sourceMounted === targetMounted) {
      continue;
    }

    const source = handlePoint(sourceNode, 'source', edge.sourceHandle);
    const target = handlePoint(targetNode, 'target', edge.targetHandle);

    result.push({
      id: edge.id,
      sourceX: source.x,
      sourceY: source.y,
      targetX: target.x,
      targetY: target.y,
      source: edge.source,
      target: edge.target,
    });
  }

  return result;
};
